// Vendeurs suivis - Réseau social
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, UserPlus, UserMinus, Star, Package, MapPin, MessageCircle,
  Bell, BellOff, ExternalLink, Search, Filter, CheckCircle, ArrowLeft, X, Store
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useCurrentUser } from '../../hooks/useCurrentUser';

function VendeurCard({ vendeur, onUnfollow, onToggleNotif, onMessage, onViewProfile, onViewAnnonce }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
            {vendeur.name.charAt(0)}
          </div>
          {vendeur.verified && (
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white">
              <CheckCircle size={12} className="text-white" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-gray-900 truncate">{vendeur.name}</h3>
            {vendeur.verified && (
              <span className="text-xs text-blue-600 font-medium">Vérifié</span>
            )}
          </div>
          
          <div className="flex items-center gap-3 text-sm text-gray-500 mb-2">
            <span className="flex items-center gap-1">
              <Star size={14} className="text-yellow-500 fill-yellow-500" />
              {vendeur.rating} ({vendeur.reviewCount})
            </span>
            <span className="flex items-center gap-1">
              <MapPin size={14} />
              {vendeur.ville}
            </span>
          </div>

          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Package size={12} />
              {vendeur.annoncesCount} annonces
            </span>
            <span className="flex items-center gap-1">
              <Users size={12} />
              {vendeur.followersCount} abonnés
            </span>
          </div>
        </div>

        {/* Boutons Voir profil + Contacter - Desktop uniquement, alignés avec les infos */}
        <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewProfile(vendeur)}
          >
            <ExternalLink size={14} className="mr-1" /> Voir le profil
          </Button>
          <Button
            size="sm"
            className="bg-orange-500 hover:bg-orange-600 text-white"
            onClick={() => onMessage(vendeur)}
          >
            <MessageCircle size={14} className="mr-1" /> Contacter
          </Button>
        </div>

        {/* Actions notif/unfollow */}
        <div className="flex flex-col gap-2 flex-shrink-0">
          <button
            onClick={() => onToggleNotif(vendeur.id)}
            className={`p-2 rounded-lg transition-colors ${
              vendeur.notifEnabled 
                ? 'bg-blue-50 text-blue-600' 
                : 'hover:bg-gray-100 text-gray-400'
            }`}
            title={vendeur.notifEnabled ? 'Notifications activées' : 'Activer les notifications'}
          >
            {vendeur.notifEnabled ? <Bell size={18} /> : <BellOff size={18} />}
          </button>
          <button
            onClick={() => onUnfollow(vendeur.id)}
            className="p-2 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-lg transition-colors"
            title="Ne plus suivre"
          >
            <UserMinus size={18} />
          </button>
        </div>
      </div>

      {/* Dernières annonces */}
      {vendeur.recentAnnonces?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 mb-2">Dernières annonces :</p>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {vendeur.recentAnnonces.map((annonce) => (
              <button
                key={annonce.id}
                onClick={() => onViewAnnonce(annonce)}
                className="flex-shrink-0 w-24 text-left"
              >
                <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden mb-1">
                  {annonce.image ? (
                    <img src={annonce.image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package size={20} className="text-gray-300" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-900 truncate">{annonce.title}</p>
                <p className="text-xs font-bold text-orange-600">{annonce.price} €</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Boutons Mobile uniquement - en bas */}
      <div className="flex lg:hidden items-center gap-2 mt-4 pt-4 border-t border-gray-100">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={() => onViewProfile(vendeur)}
        >
          <ExternalLink size={14} className="mr-1" /> Voir le profil
        </Button>
        <Button
          size="sm"
          className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
          onClick={() => onMessage(vendeur)}
        >
          <MessageCircle size={14} className="mr-1" /> Contacter
        </Button>
      </div>
    </motion.div>
  );
}

export default function VendeursSuivisPage({ onSelectTool }) {
  const { user } = useCurrentUser();
  const [vendeurs, setVendeurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'notifications', 'verified'
  
  // Modal états
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showAnnonceModal, setShowAnnonceModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedVendeur, setSelectedVendeur] = useState(null);
  const [selectedAnnonce, setSelectedAnnonce] = useState(null);
  
  // Ticket form state
  const [ticketCategory, setTicketCategory] = useState('');
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketMessage, setTicketMessage] = useState('');
  const [ticketLoading, setTicketLoading] = useState(false);
  
  const TICKET_CATEGORIES = [
    { id: 'question_produit', label: 'Question sur un produit', icon: '❓' },
    { id: 'disponibilite', label: 'Disponibilité / Stock', icon: '📦' },
    { id: 'livraison', label: 'Livraison / Expédition', icon: '🚚' },
    { id: 'negociation', label: 'Négociation de prix', icon: '💰' },
    { id: 'autre', label: 'Autre demande', icon: '✉️' },
  ];

  // Données démo avec vendeurs
  const DEMO_VENDEURS = [
    {
      id: 'v1',
      name: 'BricoTools Pro',
      verified: true,
      rating: 4.9,
      reviewCount: 127,
      ville: 'Lyon',
      annoncesCount: 45,
      followersCount: 892,
      notifEnabled: true,
      description: 'Spécialiste de l\'outillage professionnel et amateur depuis 2018. Livraison rapide et soignée.',
      recentAnnonces: [
        { id: 'a1', title: 'Perceuse Bosch Pro', price: 189, image: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=200' },
        { id: 'a2', title: 'Meuleuse Makita', price: 95, image: 'https://images.pexels.com/photos/13792037/pexels-photo-13792037.jpeg?w=200' },
        { id: 'a3', title: 'Kit tournevis 50pcs', price: 35, image: null },
      ]
    },
    {
      id: 'v2',
      name: 'Déco Maison Plus',
      verified: true,
      rating: 4.7,
      reviewCount: 89,
      ville: 'Paris',
      annoncesCount: 78,
      followersCount: 1250,
      notifEnabled: false,
      description: 'Articles de décoration et ameublement. Tendances et classiques pour votre intérieur.',
      recentAnnonces: [
        { id: 'a4', title: 'Lampe design LED', price: 65, image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200' },
        { id: 'a5', title: 'Miroir rond 80cm', price: 120, image: null },
      ]
    },
    {
      id: 'v3',
      name: 'TechnoShop',
      verified: false,
      rating: 4.5,
      reviewCount: 34,
      ville: 'Marseille',
      annoncesCount: 23,
      followersCount: 156,
      notifEnabled: true,
      description: 'High-tech et accessoires informatiques à prix compétitifs.',
      recentAnnonces: [
        { id: 'a6', title: 'Clavier mécanique RGB', price: 79, image: 'https://images.unsplash.com/photo-1587829741301-dc798b83add3?w=200' },
      ]
    },
    {
      id: 'v4',
      name: 'Mode & Style',
      verified: true,
      rating: 4.8,
      reviewCount: 203,
      ville: 'Bordeaux',
      annoncesCount: 156,
      followersCount: 2340,
      notifEnabled: false,
      description: 'Vêtements et accessoires de mode. Grandes marques et créateurs indépendants.',
      recentAnnonces: []
    },
  ];

  useEffect(() => {
    setTimeout(() => {
      setVendeurs(DEMO_VENDEURS);
      setLoading(false);
    }, 300);
  }, []);

  const handleUnfollow = (id) => {
    if (confirm('Ne plus suivre ce vendeur ?')) {
      setVendeurs(prev => prev.filter(v => v.id !== id));
    }
  };

  const handleToggleNotif = (id) => {
    setVendeurs(prev => prev.map(v => 
      v.id === id ? { ...v, notifEnabled: !v.notifEnabled } : v
    ));
  };

  const handleMessage = (vendeur) => {
    setSelectedVendeur(vendeur);
    setTicketCategory('');
    setTicketSubject('');
    setTicketMessage('');
    setShowTicketModal(true);
  };
  
  const handleViewProfile = (vendeur) => {
    setSelectedVendeur(vendeur);
    setShowProfileModal(true);
  };
  
  const handleViewAnnonce = (annonce) => {
    setSelectedAnnonce(annonce);
    setShowAnnonceModal(true);
  };
  
  const handleSubmitTicket = async () => {
    if (!ticketCategory || !ticketSubject.trim() || !ticketMessage.trim()) return;
    
    setTicketLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/tickets/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          seller_id: selectedVendeur.id,
          seller_name: selectedVendeur.name,
          category: ticketCategory,
          subject: ticketSubject,
          message: ticketMessage,
        })
      });
      
      if (response.ok) {
        setShowTicketModal(false);
        setShowSuccessModal(true);
      } else {
        alert('Erreur lors de la création du ticket');
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('Erreur de connexion');
    } finally {
      setTicketLoading(false);
    }
  };

  // Filtrage par recherche ET par filtre actif
  const filteredVendeurs = vendeurs.filter(v => {
    // Filtre par recherche texte
    const matchesSearch = v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.ville.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Filtre par type
    if (activeFilter === 'notifications') {
      return matchesSearch && v.notifEnabled;
    } else if (activeFilter === 'verified') {
      return matchesSearch && v.verified;
    }
    return matchesSearch;
  });
  
  // Labels pour le filtre actif
  const getFilterLabel = () => {
    if (activeFilter === 'notifications') return 'avec notifications';
    if (activeFilter === 'verified') return 'vérifiés';
    return '';
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        {onSelectTool && (
          <button 
            onClick={() => onSelectTool('hub_marketplace', 'Marketplace')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Retour au tableau de bord"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
        )}
        <div>
          <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Vendeurs suivis</h1>
          <p className="text-xs sm:text-sm text-gray-500">{vendeurs.length} vendeur{vendeurs.length > 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Recherche */}
      <div className="flex items-center gap-2 px-2.5 py-1.5 border border-gray-200 rounded-xl">
        <Search size={14} className="text-gray-400 flex-shrink-0" />
        <input
          type="text"
          placeholder="Rechercher un vendeur..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 bg-transparent border-0 outline-none text-xs text-gray-700 placeholder-gray-400"
        />
      </div>

      {/* Stats rapides - Cliquables pour filtrer */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <button 
          onClick={() => setActiveFilter(activeFilter === 'all' ? 'all' : 'all')}
          className={`bg-white rounded-xl p-4 border-2 transition-all text-left ${
            activeFilter === 'all' 
              ? 'border-orange-500 ring-2 ring-orange-200' 
              : 'border-gray-200 hover:border-orange-300'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Users size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{vendeurs.length}</p>
              <p className="text-xs text-gray-500">Vendeurs suivis</p>
            </div>
          </div>
        </button>
        <button 
          onClick={() => setActiveFilter(activeFilter === 'notifications' ? 'all' : 'notifications')}
          className={`bg-white rounded-xl p-4 border-2 transition-all text-left ${
            activeFilter === 'notifications' 
              ? 'border-blue-500 ring-2 ring-blue-200' 
              : 'border-gray-200 hover:border-blue-300'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Bell size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{vendeurs.filter(v => v.notifEnabled).length}</p>
              <p className="text-xs text-gray-500">Notifications</p>
            </div>
          </div>
        </button>
        <button 
          onClick={() => setActiveFilter(activeFilter === 'verified' ? 'all' : 'verified')}
          className={`bg-white rounded-xl p-4 border-2 transition-all text-left ${
            activeFilter === 'verified' 
              ? 'border-green-500 ring-2 ring-green-200' 
              : 'border-gray-200 hover:border-green-300'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{vendeurs.filter(v => v.verified).length}</p>
              <p className="text-xs text-gray-500">Vérifiés</p>
            </div>
          </div>
        </button>
      </div>

      {/* Indicateur de filtre actif */}
      {activeFilter !== 'all' && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Filtre actif :</span>
          <span className={`px-3 py-1 rounded-full font-medium ${
            activeFilter === 'notifications' 
              ? 'bg-blue-100 text-blue-700' 
              : 'bg-green-100 text-green-700'
          }`}>
            {activeFilter === 'notifications' ? 'Notifications activées' : 'Vendeurs vérifiés'}
          </span>
          <button 
            onClick={() => setActiveFilter('all')}
            className="text-gray-400 hover:text-gray-600 ml-1"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
        </div>
      ) : filteredVendeurs.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Users size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchQuery ? 'Aucun vendeur trouvé' : 'Aucun vendeur suivi'}
          </h3>
          <p className="text-gray-500 mb-4">
            {searchQuery 
              ? 'Modifie ta recherche' 
              : 'Suis des vendeurs pour voir leurs nouvelles annonces'}
          </p>
          {onSelectTool && (
            <Button 
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={() => onSelectTool('hub_marketplace', 'Marketplace')}
            >
              Découvrir des vendeurs
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredVendeurs.map((vendeur) => (
            <VendeurCard
              key={vendeur.id}
              vendeur={vendeur}
              onUnfollow={handleUnfollow}
              onToggleNotif={handleToggleNotif}
              onMessage={handleMessage}
              onViewProfile={handleViewProfile}
              onViewAnnonce={handleViewAnnonce}
            />
          ))}
        </div>
      )}

      {/* Modal Profil Vendeur */}
      <AnimatePresence>
        {showProfileModal && selectedVendeur && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] z-[9999] flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowProfileModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header avec gradient */}
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 text-white relative">
                <button 
                  onClick={() => setShowProfileModal(false)}
                  className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-lg"
                >
                  <X size={20} />
                </button>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-2xl font-bold">
                    {selectedVendeur.name.charAt(0)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold">{selectedVendeur.name}</h3>
                      {selectedVendeur.verified && (
                        <CheckCircle size={16} className="text-white" />
                      )}
                    </div>
                    <p className="text-white/80 text-sm flex items-center gap-1">
                      <MapPin size={14} /> {selectedVendeur.ville}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contenu */}
              <div className="p-6 space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-lg font-bold text-gray-900">{selectedVendeur.rating}</p>
                    <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                      <Star size={12} className="text-yellow-500 fill-yellow-500" /> Note
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-lg font-bold text-gray-900">{selectedVendeur.annoncesCount}</p>
                    <p className="text-xs text-gray-500">Annonces</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-lg font-bold text-gray-900">{selectedVendeur.followersCount}</p>
                    <p className="text-xs text-gray-500">Abonnés</p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">À propos</h4>
                  <p className="text-sm text-gray-600">{selectedVendeur.description}</p>
                </div>

                {/* Annonces récentes */}
                {selectedVendeur.recentAnnonces?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Annonces récentes</h4>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {selectedVendeur.recentAnnonces.map((annonce) => (
                        <button
                          key={annonce.id}
                          onClick={() => {
                            setShowProfileModal(false);
                            handleViewAnnonce(annonce);
                          }}
                          className="flex-shrink-0 w-20 text-left"
                        >
                          <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden mb-1">
                            {annonce.image ? (
                              <img src={annonce.image} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package size={16} className="text-gray-300" />
                              </div>
                            )}
                          </div>
                          <p className="text-xs font-bold text-orange-600">{annonce.price} €</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 p-4 border-t bg-gray-50">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowProfileModal(false)}
                >
                  Fermer
                </Button>
                <Button
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => {
                    setShowProfileModal(false);
                    handleMessage(selectedVendeur);
                  }}
                >
                  <MessageCircle size={16} className="mr-1" /> Contacter
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Créer un Ticket */}
      <AnimatePresence>
        {showTicketModal && selectedVendeur && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] z-[9999] flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowTicketModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-bold">
                      {selectedVendeur.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-semibold">Envoyer une demande</h3>
                      <p className="text-sm text-white/80">à {selectedVendeur.name}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowTicketModal(false)}
                    className="p-1 hover:bg-white/20 rounded-lg"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Contenu */}
              <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
                {/* Catégorie */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de demande *
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {TICKET_CATEGORIES.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setTicketCategory(cat.id)}
                        className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all text-left ${
                          ticketCategory === cat.id
                            ? 'border-orange-500 bg-orange-50 text-orange-700'
                            : 'border-gray-200 hover:border-orange-300'
                        }`}
                      >
                        <span className="text-lg">{cat.icon}</span>
                        <span className="text-sm font-medium">{cat.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sujet */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Objet de la demande *
                  </label>
                  <input
                    type="text"
                    value={ticketSubject}
                    onChange={(e) => setTicketSubject(e.target.value)}
                    placeholder="Ex: Question sur la Perceuse Bosch Pro"
                    className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ton message *
                  </label>
                  <textarea
                    value={ticketMessage}
                    onChange={(e) => setTicketMessage(e.target.value)}
                    placeholder="Décris ta demande en détail..."
                    className="w-full p-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    rows={4}
                  />
                </div>

                {/* Info */}
                <div className="bg-blue-50 rounded-xl p-3 text-sm text-blue-700">
                  <p className="font-medium mb-1">💡 Bon à savoir</p>
                  <p>Le vendeur recevra ta demande et te répondra dans les meilleurs délais. Tu seras notifié de sa réponse.</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 p-4 border-t bg-gray-50">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowTicketModal(false)}
                  disabled={ticketLoading}
                >
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={handleSubmitTicket}
                  disabled={!ticketCategory || !ticketSubject.trim() || !ticketMessage.trim() || ticketLoading}
                >
                  {ticketLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Envoi...
                    </span>
                  ) : (
                    'Envoyer la demande'
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Succès */}
      <AnimatePresence>
        {showSuccessModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] z-[9999] flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowSuccessModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Demande envoyée !</h3>
              <p className="text-gray-500 mb-6">
                Le vendeur a reçu ta demande et te répondra dans les meilleurs délais.
              </p>
              <Button
                className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                onClick={() => setShowSuccessModal(false)}
              >
                Compris
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Détails Annonce */}
      <AnimatePresence>
        {showAnnonceModal && selectedAnnonce && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] z-[9999] flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowAnnonceModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Image */}
              <div className="relative h-48 bg-gray-100">
                {selectedAnnonce.image ? (
                  <img src={selectedAnnonce.image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package size={48} className="text-gray-300" />
                  </div>
                )}
                <button 
                  onClick={() => setShowAnnonceModal(false)}
                  className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-full"
                >
                  <X size={20} className="text-white" />
                </button>
              </div>

              {/* Contenu */}
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{selectedAnnonce.title}</h3>
                <p className="text-2xl font-bold text-orange-600 mb-4">{selectedAnnonce.price} €</p>
                
                <p className="text-sm text-gray-600 mb-4">
                  Annonce publiée par un de tes vendeurs suivis.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 p-4 border-t bg-gray-50">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAnnonceModal(false)}
                >
                  Fermer
                </Button>
                <Button
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => {
                    setShowAnnonceModal(false);
                    alert('Fonctionnalité "Acheter" à implémenter avec le panier');
                  }}
                >
                  Acheter
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
